import React, {type ReactNode} from 'react'
import SidebarInputError from '@/Components/Sidebars/Form/SidebarInputError'
import {Field, Label, Switch} from '@headlessui/react'
import {ISidebarInputProps} from '@/Types/Input'
import {useSidebarInput} from '@/Utils/Hooks/useSidebarInput'
import classNames from '@/Utils/ClassNames'

interface Props extends ISidebarInputProps {
    checked: boolean
    onChange: (value: boolean) => void
    disabled?: boolean
    justify?: 'items-start' | 'items-center' | 'items-end'
    ariaControls?: string
    /**
     * Rendered under the tagline, in the same column, so it lines up with
     * the title rather than with the switch. Outside the <Label>, so
     * clicking it does not flip the toggle.
     */
    notice?: ReactNode
}

export default function SidebarToggle(
    {
        id: idProp,
        title,
        tagline,
        checked,
        onChange,
        error,
        className = '',
        justify,
        disabled = false,
        ariaControls,
        notice,
    }: Props): ReactNode {
    const {id, hasError} = useSidebarInput({id: idProp, title, error})
    // A notice makes the text column much taller than the switch, so top-align
    // rather than leaving the switch floating in the middle of it.
    const alignment = justify ?? (notice ? 'items-start' : 'items-center')
    // A notice usually explains why the control is disabled, so it has to
    // reach a screen reader that lands on the switch — not just sighted users.
    const describedBy = [
        hasError ? `${id}-error` : null,
        notice ? `${id}-notice` : null,
    ].filter(Boolean).join(' ') || undefined

    return <div className={className}>
        <Field className={classNames('flex', alignment)}>
            <Switch
                checked={checked}
                onChange={onChange}
                disabled={disabled}
                className={'group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus-visible:ring-2 focus-visible:ring-royal-20 focus-visible:ring-offset-2 focus-visible:outline-none data-checked:bg-royal-10 disabled:cursor-not-allowed disabled:!bg-gray-400'}
                id={id}
                aria-describedby={describedBy}
                aria-invalid={hasError ? true : undefined}
                aria-controls={ariaControls}
            >
                <span
                    className="pointer-events-none relative inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-checked:translate-x-5">
                <span
                    aria-hidden="true"
                    className="absolute inset-0 flex size-full items-center justify-center transition-opacity duration-200 ease-in group-data-checked:opacity-0 group-data-checked:duration-100 group-data-checked:ease-out"
                >
                    <svg fill="none" viewBox="0 0 12 12" className="size-3 text-gray-400">
                        <path
                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
                <span
                    aria-hidden="true"
                    className="absolute inset-0 flex size-full items-center justify-center opacity-0 transition-opacity duration-100 ease-out group-data-checked:opacity-100 group-data-checked:duration-200 group-data-checked:ease-in"
                >
                    <svg fill="currentColor" viewBox="0 0 12 12" className="size-3 text-royal-10">
                        <path
                            d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"/>
                    </svg>
                </span>
              </span>
            </Switch>
            <div className="ml-3 grow min-w-0 text-sm">
                <Label as="span">
                    <div className="font-medium text-gray-900">{title}</div>
                    {tagline && <div className="text-gray-500">{tagline}</div>}
                </Label>
                {notice && <div id={`${id}-notice`}>{notice}</div>}
            </div>
        </Field>
        <SidebarInputError id={id} error={error}/>
    </div>
}
